/**
 * Object Created Event Component
 *
 * Handles ahoy_object_created events.
 * Display patterns:
 * - Without properties: "**Actor** created this contact"
 * - With properties: "**Actor** created this contact" with expandable sub-items
 */

import { Edit03 as EditIcon } from '@untitledui/icons';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { SubjectReference } from '../components/SubjectReference';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { isStageSeedingCreate } from '../timelineItemVisibility';
import type { PropertyUpdatePayload } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import {
  buildJsonSubItems,
  getCompanionSchema,
} from '../utils/buildJsonSubItems';
import { getTimelineValueText } from '../utils/valueText';
import type { EventComponentProps } from './types';

export function ObjectCreatedEvent({
  event,
  entityType,
  isContext,
}: EventComponentProps) {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? Number(orgIdParam) : undefined;
  const { getActorInfo, formatPropertyName, getObjectLabel } =
    useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const openSlideout = useOpenSlideoutOptional();

  // Derived before early return so hooks can safely depend on them
  const isAssociatedEvent = 'association_type_name' in event;
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;
  const isNoteObject = associatedObject?.entity_type_name === 'ahoy_note';

  const handleAssociatedClick = useCallback(() => {
    if (associatedObject) {
      openSlideout?.(associatedObject.entity_id);
    }
  }, [openSlideout, associatedObject]);

  const handleEntityClick = useCallback(
    (entityId: string) => openSlideout?.(entityId),
    [openSlideout]
  );

  const associatedBadgeState = useMemo(
    () =>
      associatedObject && !isNoteObject
        ? {
            kind: 'data' as const,
            data: {
              id: associatedObject.entity_id,
              entityType: associatedObject.entity_type_name,
              displayName: associatedObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [associatedObject, isNoteObject]
  );

  // Hide stage creation events from seeding (system-generated, not user-relevant)
  if (isStageSeedingCreate(event)) {
    return null;
  }

  // Get property schema from lookups
  const getPropertySchema = (
    propertyName: string
  ): PropertyTypeSchema | undefined => {
    const key = `${event.entity_type_name}.${propertyName}`;
    return lookups.property_types[key];
  };

  // Get icon for a property using schema lookup
  const getPropertyIcon = (propertyName: string): ReactElement => {
    const iconClass = 'w-4 h-4 text-primary';
    const propertySchema = getPropertySchema(propertyName);

    if (propertySchema) {
      const IconComponent = getPropertyTypeIconComponent(propertySchema);
      return <IconComponent className={iconClass} />;
    }

    // Fallback if property not in schema
    return <EditIcon className={iconClass} />;
  };

  // For associated events, get the object label from lookups (for aria-label and fallback)
  // For primary events, use "this {entityType}"
  // Context node → the event's entity is not the record being viewed; use its
  // display name (badged below) instead of "this contact".
  const contextEntity = isContext
    ? lookups.entities[event.entity_id]
    : undefined;
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : (contextEntity?.ahoy_display_name ??
      `this ${getEntityTypeSentenceLabel(entityType)}`);
  let objectDisplay: ReactNode;
  if (associatedBadgeState) {
    objectDisplay = (
      <EntityBadge
        state={associatedBadgeState}
        className='align-middle'
        onClick={openSlideout ? handleAssociatedClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    // Primary node → "this contact"; context node → badge the subject entity.
    objectDisplay = (
      <SubjectReference
        isContext={isContext}
        entityId={event.entity_id}
        fallback={objectDisplayText}
        onEntityClick={openSlideout ? handleEntityClick : undefined}
      />
    );
  }

  // Get initial properties from payload
  const payload = event.payload as PropertyUpdatePayload | undefined;
  const properties = payload?.properties || {};

  // Filter to only show properties with value_read_access: 'all'
  const visibleProperties = Object.entries(properties).filter(([propName]) => {
    const schema = getPropertySchema(propName);
    // Only show properties where value_read_access is explicitly 'all'
    return schema?.value_read_access === 'all';
  });

  const isChromeExtension =
    event.source_type_name === 'ahoy_chrome_extension' ||
    event.source_type_name === 'ahoy_linkedin_extension';

  const ariaLabel = isChromeExtension
    ? `${actor.name} created ${objectDisplayText} via the Ahoy Chrome Extension`
    : `${actor.name} created ${objectDisplayText}`;

  const headerContent = isChromeExtension ? (
    <InlineText>
      <ActorName actor={actor} /> created {objectDisplay} via the{' '}
      {bold('Ahoy Chrome Extension')}
    </InlineText>
  ) : (
    <InlineText>
      <ActorName actor={actor} /> created {objectDisplay}
    </InlineText>
  );

  // Audit events: show ahoy_summary as rendered markdown (no icon/label chrome)
  if (event.entity_type_name === 'ahoy_audit') {
    const summaryValue = properties.ahoy_summary;
    const summaryText =
      typeof summaryValue === 'string' ? summaryValue : undefined;

    return (
      <TimelineEvent ariaLabel={ariaLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Content
          eventId={event.event_id}
          header={headerContent}
          timestamp={event.occurred_at}
        >
          {summaryText && (
            <MarkdownContent
              content={summaryText}
              size='compact'
              orgId={orgId}
              className='mt-4 text-[var(--color-text-secondary)] [&_.markdown-alert>:first-child]:hidden'
            />
          )}
        </TimelineEvent.Content>
      </TimelineEvent>
    );
  }

  // If no visible properties, show simple message
  if (visibleProperties.length === 0) {
    return (
      <TimelineEvent ariaLabel={ariaLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Header timestamp={event.occurred_at}>
          {headerContent}
        </TimelineEvent.Header>
      </TimelineEvent>
    );
  }

  // Generate sub-items for each initial property.
  // JSON properties with a companion schema are decomposed into per-field sub-items.
  const subItems: SubItemContent[] = visibleProperties.flatMap(
    ([propertyName, value]) => {
      const schema = getPropertySchema(propertyName);

      // JSON decomposition: expand into per-field sub-items
      const companionSchema = getCompanionSchema(
        schema,
        event.internal_payload
      );
      if (
        companionSchema &&
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return buildJsonSubItems(
          value as Record<string, unknown>,
          companionSchema,
          lookups
        );
      }

      const displayName = formatPropertyName(
        propertyName,
        event.entity_type_name
      );
      const icon = getPropertyIcon(propertyName);

      // Render the value using PropertyValueLabel for consistent formatting
      const valueDisplay: ReactNode = (
        <PropertyValueLabel
          value={value}
          orgId={orgId}
          propertyType={schema}
          fontSize='sm'
          className='font-medium'
          maxLength={150}
          entityProperties={properties}
        />
      );

      return {
        icon,
        text: (
          <span>
            {displayName}: {valueDisplay}
          </span>
        ),
        ariaLabel: `${displayName}: ${getTimelineValueText(value, { empty: '—' })}`,
      };
    }
  );

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={headerContent}
        timestamp={event.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
